import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AutoCompleteComponent } from '@progress/kendo-angular-dropdowns';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { timeEntryInvestigationHoursCreate, timeEntryInvestigationHoursList, timeEntryLinkedHoursCreteAll, timeEntryUnlinkedHoursCreate } from 'src/app/shared/helpers/auth-config/time-expenses.config';
import { IInvestigation } from 'src/app/shared/interfaces/investigation.interfaces';
import { SortOrder } from 'src/app/shared/modules/graphql/enums/generic.enums';
import { IApiAddTimeEntryInput, IApiInvestigation, IApiInvestigationFilterType, IApiInvestigationOrderBy, IApiInvestigationRoleNames, IApiTimeEntryType, IApiUpdateTimeEntryInput } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import { unwrapConnection } from 'src/app/shared/rxjs.pipes';
import { AuthService, InvestigationService, TimeEntryService, TimeEntryTypeService, UserService } from 'src/app/shared/services';
import {
  ADDITIONAL_ADMIN_WORK,
  ALL_BILLING_OPTIONS,
  AUTO_EQUIPMENT_EXPERT_ANALYSIS,
  BILLING_OPTIONS_MAP,
  BILLING_OPTION_SHORT_LABELS,
  CASE_REVIEW_FOR_DEPO_PREP,
  DESK_REVIEW_OC,
  EE_EXPERT_ANALYSIS,
  EE_REPORT,
  EE_TECH_REVIEW_NFPA_921,
  EVIDENCE_TECH_LAB_EXAM_ASSIST,
  EVIDENCE_TECH_REMOVAL_RETRIEVAL,
  EVIDENCE_TECH_TRANSFERRING,
  EXAM_WITH_HAZMAT,
  FIRE_PROTECTION_ENGINEER_EXAM,
  FOLLOW_UP_CASE_WORK,
  HVAC_EXPERT_ANALYSIS,
  INV_REPORT_WITH_CAPTIONED_PHOTOS,
  MASTER_ELECTRICIAN_EXPERT_ANALYSIS,
  OC_CONTINUED_WITH_JSE,
  OC_CONTINUED_WITH_LAB_EXAM,
  OC_EVIDENCE_TRANSPORT_SHIPPING,
  OC_ON_SCENE_EXAM,
  SENIOR_EE_EXPERT_ANALYSIS,
  SENIOR_EE_REPORT,
  TECH_REVIEW_NFPA_921,
  TESTIMONY_TIME,
  TRAINING_TIME,
  ADMINISTRATIVE_HOURS_TYPE_NAME,
  TRAINING_HOURS_TYPE_NAME,
  sortBillingOptions,
} from 'src/app/shared/constants/billing-options.constants';

@Component({
  selector: 'app-mobile-hours-form',
  templateUrl: './mobile-hours-form.component.html',
  styleUrls: ['./mobile-hours-form.component.scss']
})
export class MobileHoursFormComponent implements OnInit, AfterViewInit {
  @ViewChild("autoCompleteTypeId", { static: false }) public autoCompleteTypeId: AutoCompleteComponent;
  @ViewChild('drawer') drawer: DrawerComponent;
  @ViewChild("hoursForm") form: NgForm;
  @Output() stepTo = new EventEmitter<number>();
  @Output() reloadPage = new EventEmitter<boolean>(false);
  @Input() fromTab = false;
  @Input() goBackOnSubmit = false;
  @Input() investigation: IApiInvestigation = null;

  public authConfig = {
    timeEntryUnlinkedHoursCreate,
    timeEntryInvestigationHoursList,
    timeEntryInvestigationHoursCreate,
    timeEntryLinkedHoursCreteAll
  };

  public billingOptions: string[] = ALL_BILLING_OPTIONS;
  private currentUser: any;

  public _unlinked: boolean = false;
  public get unlinked(): boolean {
    return this._unlinked;
  }

  public set unlinked(val: boolean) {
    this._unlinked = val;
    this.timeEntry.TypeId = null;
    this.selectedTypeName = '';
    this.timeEntry.billingDescription = '';
    if (val === true) {
      this.timeEntry.InvestigationId = null;
      this.timeEntry.hours = 0;
      this.onBillingOptionChange(true, TRAINING_TIME);
    } else {
      this.timeEntry.hours = 0;
      this.setBillingOptions(this.currentUser);
    }
  }

  public get isTechReview(): boolean {
    return this.selectedTypeName?.toLowerCase().includes('tech review');
  }

  public investigations: IInvestigation[] = [];
  public selectedTypeName: string;
  public typeIdsearch: string;
  public timeEntryTypes: IApiTimeEntryType[] = [];
  private timeEntryTypesCopy: IApiTimeEntryType[] = [];
  private customTechDescription =
    "Technical Review in accordance with NFPA 921 section 4.6.2 Technical Review";

  public timeEntry: IApiUpdateTimeEntryInput = {
    id: null,
    TypeId: null,
    workday: new Date(),
    hours: null,
    nonBillableHours: null,
    travelHours: null,
    nonBillableTravelHours: null,
    UserId: null,
    InvestigationId: null,
    description: "",
    billingDescription: "",
    paidDate: null,
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private loader: LoaderService,
    private userService: UserService,
    private timeEntryTypeService: TimeEntryTypeService,
    private auth: AuthService,
    private location: Location,
    private investigationService: InvestigationService,
    private timeEntryService: TimeEntryService,
    private notificationService: NotificationsService
  ) { }

  ngOnInit(): void {
    this.getTimeEntryTypesAndInvestigationList();
  }

  public disabledDates = (date: Date): boolean => {
    return moment(date).isAfter();
  };

  ngAfterViewInit() {
    this.drawer.toggle(true);
    this.addBodyClass(true);
    this.cdr.detectChanges();
  }

  public addBodyClass(isOpen = true) {
    if (isOpen) {
      document.body.classList.add("kendo-dialog-open");
    } else {
      document.body.classList.remove('kendo-dialog-open');
    }
  }

  public backToStepOne() {
    this.drawer.toggle(false);
    this.stepTo.emit(1);
    this.addBodyClass(false);
  }

  public close(): void {
    this.drawer.toggle(false);
    if (this.goBackOnSubmit) {
      this.location.back();
    } else {
      this.stepTo.emit(1);
      this.reloadPage.emit(true);
    }
    this.addBodyClass(false);
  }

  // Set custom description on type change
  public typeChange(selectedId: string) {
    this.cdr.detectChanges();
    this.autoCompleteTypeId?.toggle(false);
    this.timeEntry.TypeId = selectedId || null;
    const foundType = this.timeEntryTypes.find(({ id }) => id === this.timeEntry.TypeId);
    this.selectedTypeName = foundType?.name || '';
    if (!this.timeEntry.id) {
      if (
        foundType?.name === "Investigation Hours: Tech Review Engineer" ||
        foundType?.name === "Investigation Hours: Tech Review Investigator" ||
        foundType?.name === "Investigation Hours: (CA) Tech Review Investigator"
      ) {
        this.timeEntry.description = this.customTechDescription;
      } else {
        this.timeEntry.description = "";
      }
    }
  }

  // ─── Billing Options ──────────────────────────────────────────────────────

  public isBillingOptionChecked(option: string): boolean {
    return (this.timeEntry.billingDescription || "") === option;
  }

  public onBillingOptionChange(checked: boolean, option: string): void {
    if (checked) {
      this.timeEntry.billingDescription = option;
      this.applyTimeEntryTypeForBillingOption(option);
    } else {
      this.timeEntry.billingDescription = "";
    }
  }

  public getBillingOptionLabel(option: string): string {
    return BILLING_OPTION_SHORT_LABELS[option] || option;
  }

  public onInvestigationChange(investigationId: string): void {
    this.timeEntry.InvestigationId = investigationId || null;
    // Clear billing option checkboxes and type on investigation change
    this.timeEntry.billingDescription = '';
    this.timeEntry.TypeId = null;
    this.selectedTypeName = '';
    this.setBillingOptions(this.currentUser);
  }

  private setBillingOptions(u: any): void {
    if (!u) {
      this.billingOptions = ALL_BILLING_OPTIONS;
      return;
    }

    const uniqueRoles = [
      ...new Set([
        ...(u?.PermissionRoles?.map((role: any) =>
          this.normalizeRoleName(role?.name)
        ) ?? []),
      ]),
    ];

    if (this.unlinked) {
      this.billingOptions = BILLING_OPTIONS_MAP['Unlinked'];
    } else if (uniqueRoles.length > 0) {
      const combinedOptions = (uniqueRoles as string[]).reduce((acc, role) => {
        const optionsForRole = BILLING_OPTIONS_MAP[role];
        if (optionsForRole) {
          acc.push(...optionsForRole);
        }
        return acc;
      }, [] as string[]);

      this.billingOptions = sortBillingOptions([...new Set(combinedOptions)]);
    } else {
      this.billingOptions = ALL_BILLING_OPTIONS;
    }
  }

  private applyTimeEntryTypeForBillingOption(option: string): void {
    const matchedTypeId = this.resolveTimeEntryTypeIdForBillingOption(option);
    if (matchedTypeId) {
      this.typeChange(matchedTypeId);
      return;
    }

    const userEntryType = this.currentUser?.PermissionRoles?.[0]?.TimeEntryType;
    if (!this.timeEntry?.id && userEntryType?.id) {
      this.applyFallbackTimeEntryType(userEntryType?.id, userEntryType?.name, this.currentUser?.States);
    }
  }

  private resolveTimeEntryTypeIdForBillingOption(option: string): string | null {
    const isCaliforniaLoss = this.isCaliforniaLoss();
    const isAutoRisk = this.isAutoBoatOrHeavyEquipmentRisk();
    const roleTitle = this.getCurrentRoleTitle();

    switch (option) {
      case OC_ON_SCENE_EXAM:
      case OC_CONTINUED_WITH_JSE:
      case OC_CONTINUED_WITH_LAB_EXAM:
      case INV_REPORT_WITH_CAPTIONED_PHOTOS:
        return this.findTypeIdByName(
          this.getInvReportWithCaptionType(roleTitle, isCaliforniaLoss, isAutoRisk)
        );
      case EXAM_WITH_HAZMAT:
        return this.resolveHazmatTypeId(roleTitle, isCaliforniaLoss);
      case MASTER_ELECTRICIAN_EXPERT_ANALYSIS:
        return this.findTypeIdByName(this.getMasterElectricianTypeName(isCaliforniaLoss));
      case AUTO_EQUIPMENT_EXPERT_ANALYSIS:
        return this.findTypeIdByName(this.getAutoHeavyEquipmentTypeName(isCaliforniaLoss));
      case HVAC_EXPERT_ANALYSIS:
        return this.findTypeIdByName("Investigation Hours: HVAC Hours");
      case EE_EXPERT_ANALYSIS:
        return this.findTypeIdByName(this.getElectricalEngineerTypeName(isCaliforniaLoss));
      case FIRE_PROTECTION_ENGINEER_EXAM:
        return this.findTypeIdByName("Investigation Hours: Fire Protection Engineer Hours");
      case SENIOR_EE_EXPERT_ANALYSIS:
      case SENIOR_EE_REPORT:
        return this.findTypeIdByName(this.getSrElectricalEngineerTypeName(isCaliforniaLoss));
      case EVIDENCE_TECH_REMOVAL_RETRIEVAL:
      case EVIDENCE_TECH_TRANSFERRING:
      case EVIDENCE_TECH_LAB_EXAM_ASSIST:
      case OC_EVIDENCE_TRANSPORT_SHIPPING:
        return this.findTypeIdByName(this.getEvidenceTechnicianTypeName(isCaliforniaLoss));
      case DESK_REVIEW_OC:
        return this.resolveDeskReviewTypeId(roleTitle, isCaliforniaLoss, false);
      case FOLLOW_UP_CASE_WORK:
        return this.resolveFollowUpTypeId(roleTitle, isCaliforniaLoss, isAutoRisk);
      case EE_REPORT:
        return this.resolveEeReportTypeId(roleTitle, isCaliforniaLoss);
      case CASE_REVIEW_FOR_DEPO_PREP:
        return this.resolveDepoPrepTypeId(roleTitle, isCaliforniaLoss, isAutoRisk);
      case TESTIMONY_TIME:
        return this.resolveTestimonyTypeId(roleTitle, isCaliforniaLoss, false);
      case TECH_REVIEW_NFPA_921:
        return this.resolveTechReviewTypeId(roleTitle, isCaliforniaLoss);
      case ADDITIONAL_ADMIN_WORK:
        return this.findTypeIdByName(ADMINISTRATIVE_HOURS_TYPE_NAME);
      case TRAINING_TIME:
        return this.findTypeIdByName(TRAINING_HOURS_TYPE_NAME);
      case EE_TECH_REVIEW_NFPA_921:
        return this.findTypeIdByName(this.getTechReviewEngineer(isCaliforniaLoss));
      default:
        return null;
    }
  }

  private getInvReportWithCaptionType(roleTitle: string | null, isCaliforniaLoss: boolean, isAutoRisk: boolean): string {
    if (roleTitle === IApiInvestigationRoleNames.MasterElectrician) {
      return this.getMasterElectricianTypeName(isCaliforniaLoss);
    } else {
      return this.getFireInvestigationTypeName(isCaliforniaLoss, isAutoRisk);
    }
  }

  private resolveHazmatTypeId(roleTitle: string | null, isCaliforniaLoss: boolean): string | null {
    if (roleTitle === IApiInvestigationRoleNames.ElectricalEngineer) {
      return this.findTypeIdByName(this.getElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.SrElectricalEngineer) {
      return this.findTypeIdByName(this.getSrElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (
      roleTitle === IApiInvestigationRoleNames.Investigator ||
      roleTitle === IApiInvestigationRoleNames.FormerInvestigator
    ) {
      return this.findTypeIdByName(
        isCaliforniaLoss
          ? "Investigation Hours: (CA) Hazmat Hours"
          : "Investigation Hours: Hazmat Hours"
      );
    }
    return null;
  }

  private resolveDeskReviewTypeId(roleTitle: string | null, isCaliforniaLoss: boolean, isAutoRisk: boolean): string | null {
    if (
      roleTitle === IApiInvestigationRoleNames.Investigator ||
      roleTitle === IApiInvestigationRoleNames.FormerInvestigator
    ) {
      return this.findTypeIdByName(this.getFireInvestigationTypeName(isCaliforniaLoss, isAutoRisk));
    }
    if (roleTitle === IApiInvestigationRoleNames.ElectricalEngineer) {
      return this.findTypeIdByName(this.getElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.SrElectricalEngineer) {
      return this.findTypeIdByName(this.getSrElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.HvacExpert) {
      return this.findTypeIdByName("Investigation Hours: HVAC Hours");
    }
    if (roleTitle === IApiInvestigationRoleNames.AutoExpert) {
      return this.findTypeIdByName(this.getAutoHeavyEquipmentTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.FireProtectionEngineer) {
      return this.findTypeIdByName("Investigation Hours: Fire Protection Engineer Hours");
    }
    return null;
  }

  private resolveFollowUpTypeId(roleTitle: string | null, isCaliforniaLoss: boolean, isAutoRisk: boolean): string | null {
    if (
      roleTitle === IApiInvestigationRoleNames.Investigator ||
      roleTitle === IApiInvestigationRoleNames.FormerInvestigator
    ) {
      return this.findTypeIdByName(this.getFireInvestigationTypeName(isCaliforniaLoss, isAutoRisk));
    }
    if (roleTitle === IApiInvestigationRoleNames.ElectricalEngineer) {
      return this.findTypeIdByName(this.getElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.SrElectricalEngineer) {
      return this.findTypeIdByName(this.getSrElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.MasterElectrician) {
      return this.findTypeIdByName(this.getMasterElectricianTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.HvacExpert) {
      return this.findTypeIdByName("Investigation Hours: HVAC Hours");
    }
    if (roleTitle === IApiInvestigationRoleNames.AutoExpert) {
      return this.findTypeIdByName(this.getAutoHeavyEquipmentTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.FireProtectionEngineer) {
      return this.findTypeIdByName("Investigation Hours: Fire Protection Engineer Hours");
    }
    return null;
  }

  private resolveEeReportTypeId(roleTitle: string | null, isCaliforniaLoss: boolean): string | null {
    if (roleTitle === IApiInvestigationRoleNames.ElectricalEngineer) {
      return this.findTypeIdByName(this.getElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.SrElectricalEngineer) {
      return this.findTypeIdByName(this.getSrElectricalEngineerTypeName(isCaliforniaLoss));
    }
    return null;
  }

  private resolveDepoPrepTypeId(roleTitle: string | null, isCaliforniaLoss: boolean, isAutoRisk: boolean): string | null {
    if (
      roleTitle === IApiInvestigationRoleNames.Investigator ||
      roleTitle === IApiInvestigationRoleNames.FormerInvestigator
    ) {
      return this.findTypeIdByName(this.getFireInvestigationTypeName(isCaliforniaLoss, isAutoRisk));
    }
    if (roleTitle === IApiInvestigationRoleNames.MasterElectrician) {
      return this.findTypeIdByName(this.getMasterElectricianTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.ElectricalEngineer) {
      return this.findTypeIdByName(this.getElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.SrElectricalEngineer) {
      return this.findTypeIdByName(this.getSrElectricalEngineerTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.HvacExpert) {
      return this.findTypeIdByName("Investigation Hours: HVAC Hours");
    }
    if (roleTitle === IApiInvestigationRoleNames.AutoExpert) {
      return this.findTypeIdByName(this.getAutoHeavyEquipmentTypeName(isCaliforniaLoss));
    }
    if (roleTitle === IApiInvestigationRoleNames.FireProtectionEngineer) {
      return this.findTypeIdByName("Investigation Hours: Fire Protection Engineer Hours");
    }
    return null;
  }

  private resolveTestimonyTypeId(roleTitle: string | null, isCaliforniaLoss: boolean, isAutoRisk: boolean): string | null {
    if (
      roleTitle === IApiInvestigationRoleNames.Investigator ||
      roleTitle === IApiInvestigationRoleNames.FormerInvestigator
    ) {
      return this.findTypeIdByName(this.getFireInvestigationTypeName(isCaliforniaLoss, isAutoRisk, true));
    }
    if (roleTitle === IApiInvestigationRoleNames.ElectricalEngineer) {
      return this.findTypeIdByName(this.getElectricalEngineerTypeName(isCaliforniaLoss, true));
    }
    if (roleTitle === IApiInvestigationRoleNames.FireProtectionEngineer) {
      return this.findTypeIdByName("Testimony Hours: Fire Protection Engineer Hours");
    }
    if (roleTitle === IApiInvestigationRoleNames.HvacExpert) {
      return this.findTypeIdByName("Testimony Hours: HVAC Hours");
    }
    if (roleTitle === IApiInvestigationRoleNames.MasterElectrician) {
      return this.findTypeIdByName("Testimony Hours: Master Electrician Hours");
    }
    return null;
  }

  private resolveTechReviewTypeId(roleTitle: string | null, isCaliforniaLoss: boolean): string | null {
    if (roleTitle === IApiInvestigationRoleNames.TechReviewer) {
      return this.findTypeIdByName(
        isCaliforniaLoss
          ? "Investigation Hours: (CA) Tech Review Investigator"
          : "Investigation Hours: Tech Review Investigator"
      );
    }
    if (roleTitle === IApiInvestigationRoleNames.EngineerTechReviewer) {
      return this.findTypeIdByName("Investigation Hours: Tech Review Engineer");
    }
    return null;
  }

  // ─── Type name helpers ────────────────────────────────────────────────────

  private getFireInvestigationTypeName(isCaliforniaLoss: boolean, isAutoRisk: boolean, isTestimony = false): string {
    const prefix = isTestimony ? "Testimony Hours:" : "Investigation Hours:";
    if (isAutoRisk) {
      return isCaliforniaLoss
        ? `${prefix} (CA) Auto & Heavy Equipment`
        : `${prefix} Auto & Heavy Equipment`;
    }
    return isCaliforniaLoss
      ? `${prefix} (CA) Fire Investigator Hours`
      : `${prefix} Fire Investigator Hours`;
  }

  private getElectricalEngineerTypeName(isCaliforniaLoss: boolean, isTestimony = false): string {
    const prefix = isTestimony ? "Testimony Hours:" : "Investigation Hours:";
    return isCaliforniaLoss
      ? `${prefix} (CA) Electrical Engineering Hours`
      : `${prefix} Electrical Engineering Hours`;
  }

  private getMasterElectricianTypeName(isCaliforniaLoss: boolean): string {
    return isCaliforniaLoss
      ? "Investigation Hours: (CA) Master Electrician Hours"
      : "Investigation Hours: Master Electrician Hours";
  }

  private getEvidenceTechnicianTypeName(isCaliforniaLoss: boolean): string {
    return isCaliforniaLoss
      ? "Investigation Hours: (CA) Evidence Technician Hours"
      : "Investigation Hours: Evidence Technician Hours";
  }

  private getSrElectricalEngineerTypeName(isCaliforniaLoss: boolean): string {
    return isCaliforniaLoss
      ? "Investigation Hours: (CA) Sr Electrical Engineering Hours"
      : "Investigation Hours: Sr Electrical Engineering Hours";
  }

  private getAutoHeavyEquipmentTypeName(isCaliforniaLoss: boolean): string {
    return isCaliforniaLoss
      ? "Investigation Hours: (CA) Auto & Heavy Equipment"
      : "Investigation Hours: Auto & Heavy Equipment";
  }

  private getTechReviewEngineer(isCaliforniaLoss: boolean): string {
    return isCaliforniaLoss
      ? "Investigation Hours: (CA) Tech Review Engineer"
      : "Investigation Hours: Tech Review Engineer";
  }

  // ─── Role / investigation helpers ─────────────────────────────────────────

  private getCurrentRoleTitle(): string | null {
    // Mobile: resolve from logged-in user's permission roles only
    const firstRole = this.currentUser?.PermissionRoles?.[0];
    return firstRole?.TimeEntryType?.name || null;
  }

  private isCaliforniaLoss(): boolean {
    return this.normalizeValue(this.currentUser?.States?.[0]) === "ca";
  }

  private isAutoBoatOrHeavyEquipmentRisk(): boolean {
    return false; // Not available in mobile standalone context
  }

  private findTypeIdByName(typeName: string): string | null {
    if (!typeName) {
      return null;
    }
    const matchedType = this.timeEntryTypes?.find((type) =>
      this.normalizeValue(type?.name || "") === this.normalizeValue(typeName)
    );
    return matchedType?.id || null;
  }

  private applyFallbackTimeEntryType(userEntryTypeId: string, userEntryTypeName: string, states: string[]): void {
    const match = this.findMatchingString(
      userEntryTypeName,
      this.timeEntryTypes?.filter((type) => type.id !== userEntryTypeId)?.map((type) => type.name || "")
    );
    if (states?.includes("CA") && match) {
      const matchType = this.timeEntryTypes?.find((type) => this.normalizeValue(type?.name || "") === this.normalizeValue(match));
      if (matchType?.id) {
        this.timeEntry.TypeId = matchType.id;
        this.typeChange(matchType.id);
        return;
      }
    }
    this.timeEntry.TypeId = userEntryTypeId;
    this.typeChange(this.timeEntry.TypeId);
  }

  private normalizeValue(value?: string | null): string {
    return (value || "")
      .replace(/&amp;/gi, "&")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  private findMatchingString(type: string, types: Array<string | null | undefined> = []): string | null {
    const words = new Set(this.normalizeValue(type).split(' '));
    for (const str of types) {
      const strWords = new Set(this.normalizeValue(str).split(' '));
      const isMatch = [...words].every(word => strWords.has(word));
      if (isMatch) {
        return str || null;
      }
    }
    return null;
  }

  private normalizeRoleName = (value?: string): string => {
    const normalized = value
      ?.trim()
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '') ?? '';

    return normalized === 'ADMIN'
      ? IApiInvestigationRoleNames.AdministrativeStaff
      : normalized;
  }

  // ─── Data loading ─────────────────────────────────────────────────────────

  private getTimeEntryTypesAndInvestigationList() {
    this.auth.authenticatedUser.subscribe((u) => {
      this.timeEntry.UserId = u.id;
      this.currentUser = u;
      this.setDefaultTimeEntryTypeForUser(u);
      this.loader.show$(forkJoin([
        this.timeEntryTypeService.get([]).pipe(unwrapConnection()),
        this.investigationService
          .get(
            [
              { type: IApiInvestigationFilterType.User, value: u.id },
              {
                type: IApiInvestigationFilterType.ViewOnlyNefconumber,
                value: "true",
              },
            ],
            { limit: -1, sortOrder: SortOrder.DESC, orderBy: IApiInvestigationOrderBy.CreatedAt }
          )
          .pipe(unwrapConnection())
      ])).subscribe(([timeEntryTypes, investigations]) => {
        this.timeEntryTypes = timeEntryTypes;
        this.timeEntryTypesCopy = timeEntryTypes;
        if (this.timeEntry?.TypeId) {
          this.typeChange(this.timeEntry.TypeId);
        }

        if (investigations && investigations?.length) {
          investigations = investigations?.filter(i => i.nefcoNumber);
          investigations.map((obj) => {
            obj["name"] = obj?.nefcoNumber ? obj.nefcoNumber : obj.id;
          });
          this.investigations = investigations;
        }
        if (this.investigation?.id) {
          this.timeEntry.InvestigationId = this.investigation.id;
        }
      });
    });
  }

  sortTimeEntries(userRoleEntries) {
    this.timeEntryTypes = this.timeEntryTypesCopy;
    if (userRoleEntries?.length) {
      const userRoleEntriesArray = this.timeEntryTypes?.filter(entry => userRoleEntries?.includes(entry.name)) || [];
      const tempTimeEntries = this.timeEntryTypes?.filter(entry => !userRoleEntries?.includes(entry.name)) || [];
      this.timeEntryTypes = [...userRoleEntriesArray, ...tempTimeEntries];
    }
  }

  setDefaultTimeEntryTypeForUser(u) {
    this.currentUser = u;
    const userEntryType = u?.PermissionRoles[0]?.TimeEntryType;
    const states = u?.States;
    const userRoleEntries = u?.PermissionRoles?.map(role => role?.TimeEntryType?.name);
    this.sortTimeEntries(userRoleEntries);
    this.setBillingOptions(u);
    if (!this.timeEntry?.id && userEntryType?.id) {
      const match = this.findMatchingString(userEntryType?.name, this.timeEntryTypes?.filter(type => type.id !== userEntryType?.id)?.map(type => type.name));
      if (states?.includes('CA') && match) {
        const matchType = this.timeEntryTypes?.find(t => t.name === match);
        this.timeEntry.TypeId = matchType?.id;
        this.typeChange(this.timeEntry.TypeId);
      } else {
        this.timeEntry.TypeId = userEntryType?.id;
        this.typeChange(this.timeEntry.TypeId);
      }
    }
  }

  public getStaffWithPermissionRole(id) {
    this.loader.show$(this.userService.getByIdWithPermissionRoleAndState(id)).subscribe(user => {
      this.setDefaultTimeEntryTypeForUser(user);
    });
  }

  // ─── Form validation & submission ─────────────────────────────────────────

  public isFormValid(): boolean {
    this.form.control.markAllAsTouched();

    const hasTravelHours =
      (typeof this.timeEntry.travelHours === 'number' && this.timeEntry.travelHours !== null) ||
      (typeof this.timeEntry.nonBillableTravelHours === 'number' && this.timeEntry.nonBillableTravelHours !== null);

    const isValid = this.form.form.valid && (this.isTechReview || ((hasTravelHours && !this.unlinked) || this.unlinked));

    if (!isValid && !this.isTechReview && !hasTravelHours && !this.unlinked) {
      this.notificationService.error("At least one travel hour field must be entered", "Validation Error");
    }

    return isValid;
  }

  public addTime() {
    if (!this.isFormValid()) {
      return;
    }
    const input = {
      ...this.timeEntry,
      hours: this.timeEntry.hours || 0,
      nonBillableHours: this.timeEntry.nonBillableHours || 0,
      travelHours: this.timeEntry.travelHours,
      nonBillableTravelHours: this.timeEntry.nonBillableTravelHours,
    };
    delete input.id;
    this.loader.show$(
      this.timeEntryService.add(input as IApiAddTimeEntryInput)
    ).subscribe(() => {
      this.notificationService.notify("Time entry added."),
        this.form.form.reset();
      this.close();
    }, error => {
      this.notificationService.handleTimeEntryError(error, "Error adding Time entry.");
    });
  }

}
